import { Link } from 'react-router-dom';
import { CreditCard, ArrowLeft } from 'lucide-react';

const LegalLayout = ({ title, children }) => (
  <div className="min-h-screen bg-[#020617]">
    {/* Header */}
    <header className="glass-heavy border-b border-white/5">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">MeTag</span>
          </Link>
          <Link to="/" className="text-slate-400 hover:text-white flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </header>

    {/* Content */}
    <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl font-bold text-white mb-8">{title}</h1>
      <div className="prose prose-invert max-w-none">
        {children}
      </div>
    </main>

    {/* Footer */}
    <footer className="border-t border-slate-800 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-slate-500 text-sm">
        © {new Date().getFullYear()} MeTag.cc. All rights reserved.
      </div>
    </footer>
  </div>
);

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy">
      <div className="space-y-6 text-slate-300" data-testid="privacy-policy-page">
        <p className="text-slate-400">Last updated: December 2024</p>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">1. Information We Collect</h2>
          <p>We collect information you provide directly to us, such as when you create an account, make a purchase, or contact us for support. This includes:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Name and email address</li>
            <li>Shipping address</li>
            <li>Phone number</li>
            <li>Payment information (processed securely by Razorpay)</li>
            <li>URLs you choose to link with your MeTag card</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Process and fulfill your orders</li>
            <li>Send you order confirmations and shipping updates</li>
            <li>Provide customer support</li>
            <li>Improve our products and services</li>
            <li>Send promotional communications (with your consent)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">3. Information Sharing</h2>
          <p>We do not sell, trade, or otherwise transfer your personal information to third parties except:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Service providers who assist in our operations (payment processing, shipping)</li>
            <li>When required by law or to protect our rights</li>
            <li>In connection with a business transfer or acquisition</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">4. Data Security</h2>
          <p>We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">5. Your Rights</h2>
          <p>You have the right to:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Access your personal information</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Opt-out of marketing communications</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">6. Contact Us</h2>
          <p>If you have questions about this Privacy Policy, please contact us at:</p>
          <ul className="mt-2 space-y-1">
            <li>Email: support@metag.com</li>
            <li>Phone: +91 78376 40500</li>
            <li>Address: Rajiv Enclave, SBS Nagar, Ludhiana, Pb. India - 141001</li>
          </ul>
        </section>
      </div>
    </LegalLayout>
  );
}
